import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  useDismissTutorialHint,
  useResetTutorialHints,
} from "../../services/api";
import { useTutorialContext } from "./TutorialContext";
import { TutorialHintDef, TutorialTourDef } from "./types";

/**
 * Register a DOM element as a named tutorial target.
 * Returns a ref callback — pass it to the `ref` prop of any HTML element.
 *
 * @example
 * const ref = useTutorialTarget("add-workspace-button");
 * <span ref={ref}><Button>Add New</Button></span>
 */
export function useTutorialTarget(id: string): React.RefCallback<HTMLElement> {
  const { registerTarget } = useTutorialContext();
  return useCallback(
    (el: HTMLElement | null) => registerTarget(id, el),
    // id is expected to be a stable string literal at each call site
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, registerTarget]
  );
}

/**
 * Declare a contextual hint that appears when `condition` is true.
 * The hint is permanently dismissed when the user clicks "Got it" or Escape.
 * It hides (without dismissing) when `condition` becomes false, so it
 * can reappear if the condition becomes true again in a future session.
 *
 * @example
 * useTutorialHint(
 *   { id: "first-workspace", targetId: "add-workspace-button",
 *     title: "Create a workspace", body: "Click here to get started." },
 *   workspaces.length === 0
 * );
 */
export function useTutorialHint(hintDef: TutorialHintDef, condition: boolean): void {
  const { state, dispatch } = useTutorialContext();
  const { hydrated } = state;

  // Keep a stable ref to the latest def so we never need to include the
  // object literal itself in effect deps (avoids infinite re-render loops).
  const defRef = useRef(hintDef);
  useLayoutEffect(() => {
    defRef.current = hintDef;
  });

  useEffect(() => {
    if (!condition) {
      dispatch({ type: "HIDE_HINT", id: defRef.current.id });
      return;
    }
    // `hydrated` is a dependency, not just a guard: SHOW_HINT is a no-op before
    // hydration, and this timer only fires once. Without re-running when
    // hydration lands, any hint whose condition became true during the initial
    // load would be dropped and never shown again.
    if (!hydrated) return;
    // Delay before showing so the page has time to finish rendering its sections
    // and settle into a stable layout — prevents the overlay from appearing at a
    // stale position and then jumping as content loads above the target element.
    const timer = window.setTimeout(() => {
      dispatch({ type: "SHOW_HINT", hint: defRef.current });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [condition, dispatch, hydrated]);
}

/**
 * Statuses worth a second attempt.
 *
 * A 4xx outside this set is a decision the server will reach identically next
 * time — a 401 has already survived useAuthFetch's own refresh-and-retry, and a
 * 422 means the payload is wrong — so retrying only delays the log line. No
 * status at all means the request never got an answer (network/CORS), which is
 * exactly the transient case retrying exists for.
 */
function isRetryableDismissalError(error: unknown): boolean {
  const status = (error as { status?: number } | null | undefined)?.status;
  if (typeof status !== "number") return true;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Persist a dismissal, retrying briefly before giving up.
 *
 * The POST is idempotent server-side (the handler checks for an existing row
 * before inserting), so a retry can never double-record. Retrying at all is the
 * point: a dismissal is a once-ever event, so a single dropped request used to
 * mean the hint replayed forever on every other device.
 *
 * This covers a blip, not an outage. A maintenance write-block answers 503 with
 * retry-after: 60, which outlives any backoff worth blocking a click on — that
 * dismissal is genuinely lost and the hint returns next session, because the
 * server is the source of truth and the write never happened. The failure is at
 * least loud in the console now; it used to be silent, since the
 * "fetch tutorial hint dismissal" tag suppresses toasts for every status.
 */
async function persistDismissal(
  dismissHint: (hintId: string, workspaceId?: string | null) => Promise<void>,
  hintId: string,
  workspaceId: string | null,
): Promise<void> {
  const attempts = 3;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      await dismissHint(hintId, workspaceId);
      return;
    } catch (error) {
      if (!isRetryableDismissalError(error)) {
        console.error(
          `Tutorial dismissal for "${hintId}" was rejected; it may reappear.`,
          error,
        );
        return;
      }
      if (attempt < attempts - 1) {
        await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
      }
    }
  }
  console.error(
    `Failed to persist tutorial dismissal for "${hintId}" after ${attempts} attempts; it may reappear.`,
  );
}

/**
 * Dismissal actions that both update the reducer and write to the server.
 *
 * Kept out of the reducer so it stays pure, and out of TutorialHydrator so
 * there is no outbound queue between the click and the request. The provider
 * now mounts inside AuthProvider, so the overlay can reach authFetch directly.
 */
export function useTutorialActions() {
  const { state, dispatch } = useTutorialContext();
  const dismissHint = useDismissTutorialHint();

  // Read state through a ref so both callbacks keep a stable identity —
  // TutorialOverlay subscribes to Escape with one of them in an effect dep.
  const stateRef = useRef(state);
  useLayoutEffect(() => {
    stateRef.current = state;
  });

  const dismissActive = useCallback(() => {
    const { activeHint: hint, activeTour: tour, currentWorkspaceId } =
      stateRef.current;
    dispatch({ type: "DISMISS_ACTIVE" });

    // noPersist hints are session-only by design and never reach the server.
    if (hint && !hint.noPersist) {
      void persistDismissal(
        dismissHint,
        hint.id,
        hint.scope === "workspace" ? currentWorkspaceId : null,
      );
    }
    if (tour) void persistDismissal(dismissHint, tour.id, null);
  }, [dispatch, dismissHint]);

  const nextStep = useCallback(() => {
    const { activeTour: tour, activeTourStepIndex: index } = stateRef.current;
    dispatch({ type: "NEXT_STEP" });
    // Finishing the last step retires the tour, same as skipping it.
    if (tour && index + 1 >= tour.steps.length) {
      void persistDismissal(dismissHint, tour.id, null);
    }
  }, [dispatch, dismissHint]);

  return { dismissActive, nextStep };
}

/**
 * Imperative handle for starting a tour and for the "Reset hints" action.
 *
 * Dismissing is not exposed here: the overlay owns it through
 * useTutorialActions, and no call site outside this module has ever needed to
 * dismiss a hint programmatically.
 */
export function useTutorial() {
  const { dispatch } = useTutorialContext();
  const resetServerHints = useResetTutorialHints();

  const startTour = useCallback(
    (tour: TutorialTourDef) => dispatch({ type: "START_TOUR", tour }),
    [dispatch]
  );

  // Clear the server first: if the DELETE fails, local state is left alone so
  // the next hydration doesn't silently re-suppress everything the user just
  // asked to see again. Callers invoke this fire-and-forget, so the rejection is
  // swallowed here to avoid an unhandled promise — useAuthFetch has already
  // surfaced the failure as a toast.
  const resetAllDismissed = useCallback(async () => {
    try {
      await resetServerHints();
    } catch {
      return;
    }
    dispatch({ type: "RESET_DISMISSED" });
  }, [dispatch, resetServerHints]);

  return { startTour, resetAllDismissed };
}
